import { reactive, Ref } from "vue"
import { roundsCount } from "./utils"
import { AxiosError } from "axios"

export type Error = {
    message: string,
    code?: number,
    detail?: string
}

export type ErrRef = Ref<Error>

export function parseAxiosError(msg: string, err: AxiosError): Error {
    return {
        message: msg,
        code: err.status,
        detail: err.response.data['message']
    }
}

export type MessageList = (string|string[])[]

export type Ruleset = {
    A: {
        A: number,
        B: number
    },
    B: {
        A: number,
        B: number
    }
    gameType: string,
    id: number,
}

export type RulesetData = {
    payoff_aa: number
    payoff_ab: number
    payoff_ba: number
    payoff_bb: number
    gameType: string
    id: number
}

export function makeRuleset(rs: RulesetData): Ruleset|null {
    if (!rs) {
        return null
    }

    return {
        A: {
            A: rs["payoff_aa"],
            B: rs["payoff_ab"],
        },
        B: {
            A: rs["payoff_ba"],
            B: rs["payoff_bb"],
        },
        gameType: rs["gameType"],
        id: rs["id"]
    }
}

/**
 * Names one of the two payoff tables the way the study's copy does (see the Part 2
 * instructions): the table played in Part 1 is the ORIGINALE, the other one the ALTERNATIVA.
 * Which of the session's two rulesets is which depends on the session (the first game is
 * configurable), so the comparison is against the participant's own Part 1 ruleset.
 * Returns an empty string when either id is missing, so callers can `v-if` the label away.
 */
export function rulesetLabel(rulesetId?: number, phase1RulesetId?: number): string {
    if (!rulesetId || !phase1RulesetId) {
        return ""
    }

    return rulesetId === phase1RulesetId ? "ORIGINALE" : "ALTERNATIVA"
}

/**
 * The two tables the study's copy talks about, resolved from the session payload: the ORIGINALE
 * is the one the participant plays in Part 1, the ALTERNATIVA is the session's other ruleset
 * (see rulesetLabel). Either can be null before the matching groups have a ruleset.
 */
export function studyRulesets(sessionData: Record<string, any>): { original: Ruleset|null, alternative: Ruleset|null } {
    const phase1 = sessionData["phase1Ruleset"] as RulesetData
    const other = [sessionData["pdRuleset"], sessionData["hgRuleset"]]
        .find((rs) => rs && rs.id !== phase1?.id) as RulesetData

    return {
        original: makeRuleset(phase1),
        alternative: makeRuleset(other),
    }
}

export class Phase {
    idx = -1
    ruleset : Ruleset
    completed = false
    rounds : Round[] = []
    evaluated = false

    constructor(idx) {
        this.idx = idx
    }

    get currentRound() {
        return this.rounds.find((r) => !r.choices[0] || !r.choices[1])
    }

    roundAt(idx: number): Round {
        return idx >= 0 ? this.rounds[idx] : this.rounds[this.rounds.length + idx]
    }

    addRound(r: Round) {
        this.rounds.push(r)

        return this.roundsOver
    }

    get roundsOver() {
        // rounds holds the played rounds plus the one just appended by addRound, so the
        // phase is over once we've gone past ROUNDS_PER_PHASE played rounds.
        return this.rounds.length > roundsCount()
    }

    get allMessages() {
        return this.rounds.map((r) => {
            return r.messages
        })
    }
}

// Shown in the consultant's place when the LLM failed to answer a message (the server stores an
// errored, empty reply). It closes the exchange like a real reply would — the server counts it as
// answered too — so the participant can write again or make their choice instead of waiting.
export const FAILED_REPLY_TEXT = "Il tuo assistente non è riuscito a rispondere a questo messaggio. Puoi scrivergli di nuovo o fare la tua scelta."

// A message the privacy filter rejected because it contained personal data. It was never
// delivered to the consultant; 'after' is the number of (delivered) entries in 'messages'
// that preceded the attempt, so it can be shown in its original place in the history.
export type BlockedMessage = {
    body: string,
    after: number,
}

export class Round {
    public idx = -1
    public messages : String[] = []
    public blockedMessages : BlockedMessage[] = []
    public choices = ["", ""]
    public hasChat = true
    public choicesLock = false

    constructor(idx: number, msgs: String[] = [], chat: boolean = true) {
        this.idx = idx
        this.hasChat = chat
        this.messages = msgs
    }

    scores(rules:Ruleset): Number[] {
        if (!this.hasChoices || !rules) {
            return [-1,-1]
        }

        return [
            rules[this.choices[0]][this.choices[1]],
            rules[this.choices[1]][this.choices[0]],
        ]
    }

    addMessage(txt: string) {
        this.messages.push(txt)
    }

    get hasChoices() {
        return this.choices[0] && this.choices[1]
    }

    get awaitingReply() {
        return this.messages.length > 0 && this.messages.length % 2 !== 0
    }

    get awaitingChoice() {
        return !this.choices[1]
    }

    get canChoose() {
        if (this.choicesLock) {
            return false
        }

        if (this.choices[0]) {
            return false
        }

        return (this.messages.length && !this.awaitingReply) || (!this.hasChat)
    }

    get canSendMsg(): boolean {
        return this.hasChat && !this.awaitingReply && !this.choices[0]
    }

    get empty() {
        return !(this.messages.length || this.choices[0] || this.choices[1])
    }
}

export class Session {
    public phase1 = new Phase(1)
    public phase2 = new Phase(2)
    public votedRuleset: Ruleset|null = null
    public otherActionPredictionAnswered = false
    public voteDone = false
    public questionnaireCompleted = false
    public timeRange = []
    public hasChat = true
    public chatType: string | null = null
    // The inter-phase vote is available (phase 1 finished session-wide) and not yet cast.
    // Drives the inactivity clock's `vote` stage independently of which screen the
    // participant is on, so the countdown/warning run even while they sit on the result
    // board or the "wait for everyone" modal — mirroring the server, which starts counting
    // the vote deadline the moment phase 1 completes.
    public voteAvailable = false
    public partId = -1
    // Whether the login-time (Part 1) onboarding flow has already been completed. Comes from
    // the server (participants.onboarded_at), so it is per participant rather than per
    // browser: sharing a machine, reloading or switching device all behave correctly.
    public onboardingCompleted = false

    get currentRound() {
        return this.currentPhase ? (this.currentPhase.currentRound ?? this.currentPhase.roundAt(-1)) : null
    }
    
    get currentPhase() {
        if (this.phase2.completed) {
            return null
        }

        return !this.phase1.completed ? this.phase1 : this.phase2
    }

    get allMessages() {
        return [
            this.phase1.allMessages,
            this.phase2.allMessages,
        ]
    }

    get nextRulesetVoted() {
        return Boolean(this.votedRuleset)
    }
}

export function useSessionData() {
    const state = reactive<Session>(new Session())

    function stateInit(sessionData: Object) {
        state.partId = sessionData["partId"]
        state.hasChat = sessionData["hasChat"]
        state.chatType = sessionData["chatType"]
        //FIXME: Isn't this the same as the phase 2 ruleset?
        state.votedRuleset = makeRuleset(sessionData["votedRuleset"] as RulesetData);
        state.otherActionPredictionAnswered = sessionData["otherActionPredictionAnswered"];
        state.voteDone = sessionData["voteDone"]
        state.questionnaireCompleted = sessionData["questionnaireCompleted"];
        state.onboardingCompleted = Boolean(sessionData["onboardingCompleted"]);

        state.timeRange = [
            sessionData["startTime"],
            sessionData["endTime"]
        ]

        state.phase1.ruleset = makeRuleset(sessionData["phase1Ruleset"] as RulesetData);
        state.phase1.completed = sessionData["phase1Completed"];
        // Reload during the vote-pending window (phase 1 done for everyone, vote not yet
        // done): restore the vote clock so a refresh doesn't drop the countdown.
        state.voteAvailable = Boolean(sessionData["phase1Completed"]) && !Boolean(sessionData["voteDone"]);
        state.phase1.evaluated = sessionData["phase1ConsultantEvaluated"];
        parseRounds(state.phase1, sessionData["phase1Rounds"]);

        state.phase2.ruleset = makeRuleset(sessionData["phase2Ruleset"] as RulesetData);
        state.phase2.completed = sessionData["phase2Completed"];
        state.phase2.evaluated = sessionData["phase2ConsultantEvaluated"];
        parseRounds(state.phase2, sessionData["phase2Rounds"]);
    }

    function parseRounds(phase: Phase, rnd_data: Object[]) {
        let foundFinal = false
        rnd_data.some((r, i) => {
            const msgs = []
            const blocked = []
            if (state.hasChat){
                r["messages"].forEach((msg) => {
                    // Flagged messages were never delivered: keep them aside (anchored to
                    // their place in the delivered stream) instead of in the chat flow.
                    if (msg.blocked) {
                        blocked.push({ body: msg.body, after: msgs.length })
                        return
                    }

                    msgs.push(msg.body)

                    if (msg.hasError)
                        msgs.push(FAILED_REPLY_TEXT)
                    else if (msg.reply)
                        msgs.push(msg.reply)
                })
            }

            const round = new Round(i, msgs, state.hasChat)
            round.blockedMessages = blocked

            if (r["actionChosen"]) {
                round.choices[0] = r["actionChosen"]
            }
            if (r["actionChosenByOther"]) {
                round.choices[1] = r["actionChosenByOther"]
            }

            phase.addRound(round)      
            
            foundFinal = !round.hasChoices

            return !round.hasChoices
        })

        // Append a fresh round to play only if the phase still has rounds left.
        if (!foundFinal && phase.rounds.length < roundsCount()) {
            phase.addRound(new Round(phase.rounds.length, [], state.hasChat))
        }
    }

    return {
        state,
        stateInit
    }
}